import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import { restoreStock } from '../../utils/stockManager';
import { useCart } from '../../context/CartContext';
import WriteReviewModal from './components/WriteReviewModal';

interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  category: string;
}

interface TrackingEvent {
  status: string;
  description: string;
  location: string;
  timestamp: string;
}

interface DeliveryInfo {
  courier: string;
  trackingNumber: string;
  estimatedDate?: string;
  events: TrackingEvent[];
}

interface Order {
  orderNumber: string;
  date: string;
  status: 'pending' | 'confirmed' | 'shipping' | 'delivered' | 'cancelled';
  paymentMethod: string;
  items: OrderItem[];
  totalPrice: number;
  shippingAddress: string;
  name: string;
  phone: string;
  email: string;
  memo?: string;
  cancelReason?: string;
  deliveryInfo?: DeliveryInfo;
}

const STATUS_LABEL: Record<Order['status'], string> = {
  pending: '입금 대기',
  confirmed: '주문 확인',
  shipping: '배송 중',
  delivered: '배송 완료',
  cancelled: '주문 취소',
};

const STATUS_COLOR: Record<Order['status'], string> = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  shipping: 'bg-sky-100 text-sky-700',
  delivered: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-600',
};

const STATUS_ICON: Record<Order['status'], string> = {
  pending: 'ri-time-line',
  confirmed: 'ri-checkbox-circle-line',
  shipping: 'ri-truck-line',
  delivered: 'ri-map-pin-2-line',
  cancelled: 'ri-close-circle-line',
};

const NEXT_STATUS_OPTIONS: Record<Order['status'], Order['status'][]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['shipping', 'cancelled'],
  shipping: ['delivered', 'cancelled'],
  delivered: [],
  cancelled: [],
};

const STEPS: { key: Order['status']; label: string; icon: string }[] = [
  { key: 'confirmed', label: '주문 확인', icon: 'ri-checkbox-circle-line' },
  { key: 'shipping', label: '배송 중', icon: 'ri-truck-line' },
  { key: 'delivered', label: '배송 완료', icon: 'ri-map-pin-2-line' },
];

const CANCEL_REASONS = ['단순 변심', '배송 지연', '상품 정보 상이', '중복 주문', '기타'];

const COURIERS = [
  { name: 'CJ대한통운', code: 'cj', trackUrl: 'https://www.cjlogistics.com/ko/tool/parcel/tracking?gnbInvcNo=' },
  { name: '롯데택배', code: 'lotte', trackUrl: 'https://www.lotteglogis.com/home/reservation/tracking/linkView?InvNo=' },
  { name: '한진택배', code: 'hanjin', trackUrl: 'https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mCode=MN038&schLang=KR&wblnumText2=' },
  { name: '우체국택배', code: 'epost', trackUrl: 'https://service.epost.go.kr/trace.RetrieveEmsRigiTraceList.comm?POST_CODE=' },
  { name: '로젠택배', code: 'logen', trackUrl: 'http://www.ilogen.com/web/personal/trace/' },
  { name: '쿠팡로켓', code: 'coupang', trackUrl: '' },
  { name: '기타', code: 'etc', trackUrl: '' },
];

const CANCELLABLE_STATUSES: Order['status'][] = ['pending', 'confirmed'];

function getStepIndex(status: Order['status']): number {
  if (status === 'confirmed') return 0;
  if (status === 'shipping') return 1;
  if (status === 'delivered') return 2;
  return -1;
}

function getStepTimestamp(order: Order, stepKey: Order['status']): string | null {
  if (!order.deliveryInfo?.events) return null;
  const map: Record<string, string> = {
    confirmed: '주문 확인',
    shipping: '배송 출발',
    delivered: '배송 완료',
  };
  const label = map[stepKey];
  const ev = order.deliveryInfo.events.find((e) => e.status === label);
  return ev ? ev.timestamp : null;
}

// 배송 추적 입력 팝업
function TrackingInputModal({
  order,
  onClose,
  onSave,
}: {
  order: Order;
  onClose: () => void;
  onSave: (info: DeliveryInfo) => void;
}) {
  const existing = order.deliveryInfo;
  const [courier, setCourier] = useState(existing?.courier || COURIERS[0].name);
  const [trackingNumber, setTrackingNumber] = useState(existing?.trackingNumber || '');
  const [estimatedDate, setEstimatedDate] = useState(existing?.estimatedDate || '');
  const [events, setEvents] = useState<TrackingEvent[]>(
    existing?.events || [
      { status: '주문 확인', description: '주문이 확인되었습니다.', location: '물류센터', timestamp: order.date },
    ]
  );
  const [newEvent, setNewEvent] = useState<TrackingEvent>({
    status: '배송 출발',
    description: '',
    location: '',
    timestamp: new Date().toISOString().slice(0, 16),
  });
  const [addingEvent, setAddingEvent] = useState(false);

  const handleAddEvent = () => {
    if (!newEvent.status || !newEvent.timestamp) return;
    setEvents((prev) => [...prev, { ...newEvent }]);
    setNewEvent({ status: '', description: '', location: '', timestamp: new Date().toISOString().slice(0, 16) });
    setAddingEvent(false);
  };

  const handleRemoveEvent = (idx: number) => {
    setEvents((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (!trackingNumber.trim()) return;
    onSave({ courier, trackingNumber: trackingNumber.trim(), estimatedDate, events });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto animate-fadeIn">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-truck-line text-sky-500 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">배송 정보 입력</h3>
              <p className="text-xs text-gray-500 mt-0.5">주문번호: {order.orderNumber}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer transition-colors">
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>

        <div className="space-y-4">
          {/* 택배사 선택 */}
          <div>
            <label className="text-xs font-bold text-gray-600 mb-1.5 block">택배사</label>
            <select
              value={courier}
              onChange={(e) => setCourier(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white cursor-pointer"
            >
              {COURIERS.map((c) => (
                <option key={c.code} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* 운송장 번호 */}
          <div>
            <label className="text-xs font-bold text-gray-600 mb-1.5 block">운송장 번호</label>
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="운송장 번호를 입력하세요"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
          </div>

          {/* 배송 예정일 */}
          <div>
            <label className="text-xs font-bold text-gray-600 mb-1.5 block">배송 예정일 <span className="text-gray-400 font-normal">(선택)</span></label>
            <input
              type="date"
              value={estimatedDate}
              onChange={(e) => setEstimatedDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 cursor-pointer"
            />
          </div>

          {/* 배송 이벤트 타임라인 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-gray-600">배송 이력</label>
              <button
                onClick={() => setAddingEvent(true)}
                className="text-xs text-sky-600 font-semibold flex items-center gap-1 hover:text-sky-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line w-3.5 h-3.5 flex items-center justify-center"></i>
                이력 추가
              </button>
            </div>

            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {events.map((ev, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-gray-50 rounded-xl px-3 py-2.5">
                  <div className="w-2 h-2 rounded-full bg-sky-400 mt-1.5 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-gray-800">{ev.status}</span>
                      {ev.location && <span className="text-xs text-gray-500">{ev.location}</span>}
                    </div>
                    {ev.description && <p className="text-xs text-gray-500 mt-0.5">{ev.description}</p>}
                    <p className="text-xs text-gray-400 mt-0.5">{ev.timestamp?.replace('T', ' ')}</p>
                  </div>
                  {idx > 0 && (
                    <button onClick={() => handleRemoveEvent(idx)} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-400 cursor-pointer flex-shrink-0">
                      <i className="ri-delete-bin-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {addingEvent && (
              <div className="mt-3 bg-sky-50 border border-sky-200 rounded-xl p-3 space-y-2">
                <p className="text-xs font-bold text-sky-700 mb-2">새 이력 추가</p>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newEvent.status}
                    onChange={(e) => setNewEvent((p) => ({ ...p, status: e.target.value }))}
                    placeholder="상태 (예: 배송 출발)"
                    className="px-2.5 py-2 border border-sky-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-sky-400"
                  />
                  <input
                    type="text"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent((p) => ({ ...p, location: e.target.value }))}
                    placeholder="위치 (예: 서울 허브)"
                    className="px-2.5 py-2 border border-sky-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-sky-400"
                  />
                </div>
                <input
                  type="text"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent((p) => ({ ...p, description: e.target.value }))}
                  placeholder="설명 (선택)"
                  className="w-full px-2.5 py-2 border border-sky-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-sky-400"
                />
                <input
                  type="datetime-local"
                  value={newEvent.timestamp}
                  onChange={(e) => setNewEvent((p) => ({ ...p, timestamp: e.target.value }))}
                  className="w-full px-2.5 py-2 border border-sky-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-sky-400 cursor-pointer"
                />
                <div className="flex gap-2">
                  <button onClick={() => setAddingEvent(false)} className="flex-1 py-2 border border-gray-200 text-gray-500 rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer whitespace-nowrap">취소</button>
                  <button onClick={handleAddEvent} className="flex-1 py-2 bg-sky-500 text-white rounded-lg text-xs font-semibold hover:bg-sky-600 cursor-pointer whitespace-nowrap">추가</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">취소</button>
          <button
            onClick={handleSave}
            disabled={!trackingNumber.trim()}
            className="flex-1 py-3 bg-sky-500 text-white rounded-xl text-sm font-semibold hover:bg-sky-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <i className="ri-save-line w-4 h-4 flex items-center justify-center"></i>
            저장하기
          </button>
        </div>
      </div>
    </div>
  );
}

// 배송 추적 타임라인 컴포넌트
function DeliveryTimeline({ order, onEdit }: { order: Order; onEdit: () => void }) {
  const info = order.deliveryInfo;
  const courierObj = COURIERS.find((c) => c.name === info?.courier);
  const trackUrl = courierObj?.trackUrl ? `${courierObj.trackUrl}${info?.trackingNumber}` : '';

  if (!info) {
    return (
      <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-truck-line text-sky-500 w-4 h-4 flex items-center justify-center"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-sky-800">배송 정보가 아직 없습니다</p>
            <p className="text-xs text-sky-600 mt-0.5">운송장 번호를 입력하면 배송 현황을 추적할 수 있습니다.</p>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="px-3 py-2 bg-sky-500 text-white rounded-lg text-xs font-semibold hover:bg-sky-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 flex-shrink-0"
        >
          <i className="ri-add-line w-3.5 h-3.5 flex items-center justify-center"></i>
          입력
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* 택배사 & 운송장 정보 */}
      <div className="bg-sky-50 border border-sky-100 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-truck-line text-sky-500 w-4 h-4 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-sky-800">{info.courier}</span>
                <span className="text-xs bg-sky-200 text-sky-700 px-2 py-0.5 rounded-full font-mono font-semibold">{info.trackingNumber}</span>
              </div>
              {info.estimatedDate && (
                <p className="text-xs text-sky-600 mt-0.5 flex items-center gap-1">
                  <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                  배송 예정일: <strong>{info.estimatedDate}</strong>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {trackUrl && (
              <a
                href={trackUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="px-3 py-2 bg-sky-500 text-white rounded-lg text-xs font-semibold hover:bg-sky-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-external-link-line w-3.5 h-3.5 flex items-center justify-center"></i>
                조회하기
              </a>
            )}
            <button
              onClick={onEdit}
              className="px-3 py-2 bg-white border border-sky-200 text-sky-600 rounded-lg text-xs font-semibold hover:bg-sky-50 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-edit-line w-3.5 h-3.5 flex items-center justify-center"></i>
              수정
            </button>
          </div>
        </div>
      </div>

      {/* 배송 이력 타임라인 */}
      {info.events && info.events.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">배송 이력</p>
          <div className="relative">
            <div className="absolute left-3.5 top-0 bottom-0 w-px bg-gray-200"></div>
            <div className="space-y-4">
              {[...info.events].reverse().map((ev, idx) => {
                const isFirst = idx === 0;
                return (
                  <div key={idx} className="flex items-start gap-4 pl-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10 relative
                      ${isFirst ? 'bg-sky-500 shadow-md shadow-sky-200' : 'bg-gray-200'}`}>
                      <i className={`${isFirst ? 'ri-map-pin-2-fill text-white' : 'ri-checkbox-blank-circle-fill text-gray-400'} w-3 h-3 flex items-center justify-center`}></i>
                    </div>
                    <div className="flex-1 min-w-0 pb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-bold ${isFirst ? 'text-sky-700' : 'text-gray-700'}`}>{ev.status}</span>
                        {ev.location && (
                          <span className="text-xs text-gray-400 flex items-center gap-0.5">
                            <i className="ri-map-pin-line w-3 h-3 flex items-center justify-center"></i>
                            {ev.location}
                          </span>
                        )}
                      </div>
                      {ev.description && <p className="text-xs text-gray-500 mt-0.5">{ev.description}</p>}
                      <p className="text-xs text-gray-400 mt-0.5">{ev.timestamp?.replace('T', ' ')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrderHistoryPage() {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<Order['status'] | 'all'>('all');
  const [changingStatus, setChangingStatus] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error'; action?: { label: string; onClick: () => void } } | null>(null);
  const [cancelPopup, setCancelPopup] = useState<{ orderNumber: string } | null>(null);
  const [cancelReason, setCancelReason] = useState('단순 변심');
  const [cancelReasonEtc, setCancelReasonEtc] = useState('');
  const [trackingModal, setTrackingModal] = useState<string | null>(null);
  const [refundingOrder, setRefundingOrder] = useState<string | null>(null);
  const [reorderingOrder, setReorderingOrder] = useState<string | null>(null);
  const [reviewModalItem, setReviewModalItem] = useState<{ id: string; name: string; image: string } | null>(null);
  const [reviewedItems, setReviewedItems] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('reviewed_items');
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch { return new Set(); }
  });

  useEffect(() => {
    const saved = localStorage.getItem('orders');
    if (saved) {
      try {
        const parsed: Order[] = JSON.parse(saved);
        setOrders(parsed.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      } catch {
        setOrders([]);
      }
    }
  }, []);

  const showToast = (message: string, type: 'success' | 'error' = 'success', action?: { label: string; onClick: () => void }) => {
    setToast({ message, type, action });
    setTimeout(() => setToast(null), 4000);
  };

  const handleReorder = async (order: Order) => {
    setReorderingOrder(order.orderNumber);
    await new Promise((r) => setTimeout(r, 600));

    order.items.forEach((item) => {
      addToCart(
        {
          id: item.id,
          name: item.name,
          price: item.price,
          image: item.image,
          category: item.category,
        },
        item.quantity,
      );
    });

    setReorderingOrder(null);
    showToast(
      `${order.items.length}가지 상품이 장바구니에 담겼습니다!`,
      'success',
      { label: '장바구니 보기', onClick: () => navigate('/cart') },
    );
  };

  const handleStatusChange = (orderNumber: string, newStatus: Order['status']) => {
    if (newStatus === 'cancelled') {
      setCancelPopup({ orderNumber });
      setCancelReason('단순 변심');
      setCancelReasonEtc('');
      return;
    }
    applyStatusChange(orderNumber, newStatus);
  };

  const applyStatusChange = (orderNumber: string, newStatus: Order['status'], reason?: string) => {
    const now = new Date().toISOString().slice(0, 16);
    const updated = orders.map((o) => {
      if (o.orderNumber !== orderNumber) return o;
      const updatedOrder = { ...o, status: newStatus, cancelReason: reason };
      // 배송 중으로 변경 시 이벤트 자동 추가
      if (newStatus === 'shipping' && o.deliveryInfo) {
        const alreadyHas = o.deliveryInfo.events.some((e) => e.status === '배송 출발');
        if (!alreadyHas) {
          updatedOrder.deliveryInfo = {
            ...o.deliveryInfo,
            events: [...o.deliveryInfo.events, { status: '배송 출발', description: '상품이 배송을 시작했습니다.', location: '물류센터', timestamp: now }],
          };
        }
      }
      if (newStatus === 'delivered' && o.deliveryInfo) {
        const alreadyHas = o.deliveryInfo.events.some((e) => e.status === '배송 완료');
        if (!alreadyHas) {
          updatedOrder.deliveryInfo = {
            ...o.deliveryInfo,
            events: [...(updatedOrder.deliveryInfo?.events || o.deliveryInfo.events), { status: '배송 완료', description: '상품이 배송 완료되었습니다.', location: '배송지', timestamp: now }],
          };
        }
      }
      return updatedOrder;
    });
    setOrders(updated);
    localStorage.setItem('orders', JSON.stringify(updated));
    setChangingStatus(null);
    showToast(`주문 상태가 "${STATUS_LABEL[newStatus]}"(으)로 변경되었습니다.`);
  };

  const handleCancelConfirm = async () => {
    if (!cancelPopup) return;
    const finalReason = cancelReason === '기타' ? cancelReasonEtc.trim() : cancelReason;
    if (cancelReason === '기타' && !finalReason) {
      showToast('취소 사유를 입력해주세요.', 'error');
      return;
    }

    // 취소 대상 주문 찾기
    const targetOrder = orders.find((o) => o.orderNumber === cancelPopup.orderNumber);
    if (!targetOrder) {
      showToast('주문을 찾을 수 없습니다.', 'error');
      setCancelPopup(null);
      return;
    }

    // 카드결제인 경우 PG 환불 API 호출
    if (targetOrder.paymentMethod === '카드결제') {
      setRefundingOrder(targetOrder.orderNumber);
      
      try {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
        const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

        const response = await fetch(`${supabaseUrl}/functions/v1/payletter-payment/cancel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            order_no: targetOrder.orderNumber,
            cancel_message: finalReason,
            cancel_type: 'full'
          }),
        });

        const data = await response.json();

        if (data.success) {
          // PG 환불 성공 - 재고 복구 및 주문 상태 변경
          restoreStock(targetOrder.items.map((i) => ({ id: i.id, quantity: i.quantity })));
          applyStatusChange(cancelPopup.orderNumber, 'cancelled', finalReason);
          showToast('결제가 취소되고 환불이 완료되었습니다.');
        } else {
          // PG 환불 실패
          showToast(data.error || '결제 취소에 실패했습니다. 고객센터로 문의해주세요.', 'error');
        }
      } catch (err) {
        console.error('환불 요청 오류:', err);
        showToast('환불 요청 중 오류가 발생했습니다. 고객센터로 문의해주세요.', 'error');
      } finally {
        setRefundingOrder(null);
        setCancelPopup(null);
      }
    } else {
      // 계좌이체 등 다른 결제수단은 기존 로직대로 처리
      restoreStock(targetOrder.items.map((i) => ({ id: i.id, quantity: i.quantity })));
      applyStatusChange(cancelPopup.orderNumber, 'cancelled', finalReason);
      setCancelPopup(null);
    }
  };

  const handleSaveDelivery = (orderNumber: string, info: DeliveryInfo) => {
    const updated = orders.map((o) =>
      o.orderNumber === orderNumber ? { ...o, deliveryInfo: info } : o
    );
    setOrders(updated);
    localStorage.setItem('orders', JSON.stringify(updated));
    setTrackingModal(null);
    showToast('배송 정보가 저장되었습니다.');
  };

  const handleReviewSubmitted = (itemId: string) => {
    setReviewedItems((prev) => {
      const next = new Set([...prev, itemId]);
      localStorage.setItem('reviewed_items', JSON.stringify([...next]));
      return next;
    });
    showToast('리뷰가 등록됐어요! 제품 페이지에서 확인하실 수 있어요.', 'success');
  };

  const formatPrice = (price: number) => price.toLocaleString('ko-KR');
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const filtered = orders.filter((o) => {
    const matchStatus = filterStatus === 'all' || o.status === filterStatus;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      o.orderNumber.toLowerCase().includes(q) ||
      o.name.toLowerCase().includes(q) ||
      o.items.some((i) => i.name.toLowerCase().includes(q));
    return matchStatus && matchSearch;
  });

  const trackingOrder = trackingModal ? orders.find((o) => o.orderNumber === trackingModal) : null;

  return (
    <>
      <Navbar />

      {/* 리뷰 작성 모달 */}
      {reviewModalItem && (
        <WriteReviewModal
          item={reviewModalItem}
          onClose={() => setReviewModalItem(null)}
          onSubmitted={() => handleReviewSubmitted(reviewModalItem.id)}
        />
      )}

      {/* 배송 정보 입력 모달 */}
      {trackingOrder && (
        <TrackingInputModal
          order={trackingOrder}
          onClose={() => setTrackingModal(null)}
          onSave={(info) => handleSaveDelivery(trackingOrder.orderNumber, info)}
        />
      )}

      {/* 취소 사유 입력 팝업 */}
      {cancelPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fadeIn">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-close-circle-line text-red-500 w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">주문 취소</h3>
                <p className="text-xs text-gray-500 mt-0.5">취소 사유를 선택해주세요</p>
              </div>
            </div>

            {/* 취소 불가 안내 */}
            {(() => {
              const targetOrder = orders.find((o) => o.orderNumber === cancelPopup.orderNumber);
              if (targetOrder && !CANCELLABLE_STATUSES.includes(targetOrder.status)) {
                return (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                    <i className="ri-error-warning-line text-red-500 w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-bold text-red-700">취소가 불가능한 주문입니다</p>
                      <p className="text-xs text-red-600 mt-1">배송이 시작된 주문은 취소할 수 없습니다. 고객센터로 문의해주세요.</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* 카드결제 환불 안내 */}
            {(() => {
              const targetOrder = orders.find((o) => o.orderNumber === cancelPopup.orderNumber);
              if (targetOrder?.paymentMethod === '카드결제') {
                return (
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                    <i className="ri-information-line text-sky-500 w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-bold text-sky-700">카드결제 자동 환불</p>
                      <p className="text-xs text-sky-600 mt-1">주문 취소 시 결제하신 카드로 자동 환불됩니다. 환불 처리는 영업일 기준 3~5일 소요됩니다.</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div className="space-y-2 mb-4">
              {CANCEL_REASONS.map((reason) => (
                <label
                  key={reason}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all
                    ${cancelReason === reason ? 'border-red-400 bg-red-50' : 'border-gray-100 hover:border-gray-200 bg-gray-50'}`}
                >
                  <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={() => setCancelReason(reason)} className="accent-red-500 w-4 h-4 flex-shrink-0" />
                  <span className={`text-sm font-medium ${cancelReason === reason ? 'text-red-700' : 'text-gray-700'}`}>{reason}</span>
                </label>
              ))}
            </div>
            {cancelReason === '기타' && (
              <div className="mb-4">
                <textarea
                  value={cancelReasonEtc}
                  onChange={(e) => { if (e.target.value.length <= 200) setCancelReasonEtc(e.target.value); }}
                  placeholder="취소 사유를 직접 입력해주세요. (최대 200자)"
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 resize-none text-gray-800 placeholder-gray-400"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{cancelReasonEtc.length}/200</p>
              </div>
            )}
            <div className="flex gap-3 mt-2">
              <button 
                onClick={() => setCancelPopup(null)} 
                disabled={refundingOrder === cancelPopup.orderNumber}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                돌아가기
              </button>
              <button 
                onClick={handleCancelConfirm} 
                disabled={refundingOrder === cancelPopup.orderNumber}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refundingOrder === cancelPopup.orderNumber ? (
                  <>
                    <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                    환불 처리 중...
                  </>
                ) : (
                  <>
                    <i className="ri-close-circle-line w-4 h-4 flex items-center justify-center"></i>
                    주문 취소 확인
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 알림 */}
      {toast && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full shadow-lg text-sm font-semibold flex items-center gap-3 transition-all animate-fadeIn
          ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
          <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} w-4 h-4 flex items-center justify-center flex-shrink-0`}></i>
          <span>{toast.message}</span>
          {toast.action && (
            <button
              type="button"
              onClick={() => { toast.action!.onClick(); setToast(null); }}
              className="ml-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer transition-colors border border-white/30"
            >
              {toast.action.label}
            </button>
          )}
        </div>
      )}

      <div className="min-h-screen bg-gray-50 pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {/* 헤더 */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              주문 내역
            </h1>
            <p className="text-sm text-gray-500">주문하신 상품의 배송 현황을 확인하고 상태를 변경할 수 있습니다.</p>
          </div>

          {/* 검색 & 필터 */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="주문번호, 이름, 상품명으로 검색"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {(['all', 'pending', 'confirmed', 'shipping', 'delivered', 'cancelled'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer
                    ${filterStatus === s ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-emerald-400'}`}
                >
                  {s === 'all' ? '전체' : STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {/* 주문 없음 */}
          {filtered.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
                <i className="ri-file-list-3-line text-3xl text-gray-400 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-gray-500 text-sm mb-6">
                {orders.length === 0 ? '아직 주문 내역이 없습니다.' : '검색 결과가 없습니다.'}
              </p>
              {orders.length === 0 && (
                <Link to="/products" className="inline-block px-6 py-3 bg-emerald-600 text-white rounded-full font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  제품 둘러보기
                </Link>
              )}
            </div>
          )}

          {/* 주문 목록 */}
          <div className="space-y-4">
            {filtered.map((order) => {
              const isExpanded = expandedOrder === order.orderNumber;
              const stepIdx = getStepIndex(order.status);
              const nextOptions = NEXT_STATUS_OPTIONS[order.status];
              const isChanging = changingStatus === order.orderNumber;

              return (
                <div key={order.orderNumber} className="bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-300">
                  {/* 주문 헤더 */}
                  <button
                    onClick={() => setExpandedOrder(isExpanded ? null : order.orderNumber)}
                    className="w-full text-left px-5 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100">
                          <img src={order.items[0]?.image} alt={order.items[0]?.name} className="w-full h-full object-cover object-top" />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 mb-0.5">{formatDate(order.date)}</p>
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {order.items[0]?.name}
                          {order.items.length > 1 && <span className="text-gray-500 font-normal"> 외 {order.items.length - 1}건</span>}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">주문번호: {order.orderNumber}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-base font-bold text-emerald-600">{formatPrice(order.totalPrice)}원</p>
                        <p className="text-xs text-gray-400">{order.paymentMethod}</p>
                      </div>
                      {/* 배송 중일 때 운송장 뱃지 */}
                      {order.status === 'shipping' && order.deliveryInfo && (
                        <span className="hidden md:flex items-center gap-1 px-2 py-1 bg-sky-50 border border-sky-200 rounded-full text-xs text-sky-600 font-mono">
                          <i className="ri-truck-line w-3 h-3 flex items-center justify-center"></i>
                          {order.deliveryInfo.trackingNumber}
                        </span>
                      )}
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${STATUS_COLOR[order.status]}`}>
                        <i className={`${STATUS_ICON[order.status]} w-3 h-3 flex items-center justify-center`}></i>
                        {STATUS_LABEL[order.status]}
                      </span>
                      <i className={`ri-arrow-down-s-line text-gray-400 text-lg w-5 h-5 flex items-center justify-center transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}></i>
                    </div>
                  </button>

                  {/* 상세 내용 */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 px-5 md:px-6 py-5 space-y-6 animate-fadeIn">
                      {/* 배송 진행 단계 (취소/입금대기 제외) */}
                      {order.status !== 'cancelled' && order.status !== 'pending' && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">배송 현황</p>
                          <div className="flex items-start gap-0">
                            {STEPS.map((step, idx) => {
                              const done = idx <= stepIdx;
                              const active = idx === stepIdx;
                              const ts = getStepTimestamp(order, step.key);
                              return (
                                <div key={step.key} className="flex items-center flex-1">
                                  <div className="flex flex-col items-center flex-shrink-0">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all
                                      ${active ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200' : done ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                                      {done
                                        ? <i className={`${step.icon} w-4 h-4 flex items-center justify-center`}></i>
                                        : <span className="text-xs font-bold">{idx + 1}</span>
                                      }
                                    </div>
                                    <p className="text-xs mt-1.5 font-semibold whitespace-nowrap text-emerald-600">
                                      {step.label}
                                    </p>
                                    {ts && (
                                      <p className="text-xs text-gray-400 mt-0.5 whitespace-nowrap" style={{ fontSize: '10px' }}>
                                        {ts.replace('T', ' ')}
                                      </p>
                                    )}
                                  </div>
                                  {idx < STEPS.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-1 mb-8 ${idx < stepIdx ? 'bg-emerald-400' : 'bg-gray-200'}`}></div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* 배송 추적 섹션 (배송 중 / 배송 완료) */}
                      {(order.status === 'shipping' || order.status === 'delivered') && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">배송 추적</p>
                          <DeliveryTimeline order={order} onEdit={() => setTrackingModal(order.orderNumber)} />
                        </div>
                      )}

                      {/* 상태 변경 영역 */}
                      {nextOptions.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">주문 상태 변경</p>
                          {!isChanging ? (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${STATUS_COLOR[order.status]}`}>
                                  <i className={`${STATUS_ICON[order.status]} w-3 h-3 flex items-center justify-center`}></i>
                                  {STATUS_LABEL[order.status]}
                                </span>
                                <i className="ri-arrow-right-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                                <span className="text-xs text-gray-500">변경 가능</span>
                              </div>
                              <button
                                onClick={() => setChangingStatus(order.orderNumber)}
                                className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:border-emerald-500 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                              >
                                <i className="ri-edit-line w-3.5 h-3.5 flex items-center justify-center"></i>
                                상태 변경
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-xs text-gray-600">변경할 상태를 선택해주세요:</p>
                              <div className="flex flex-wrap gap-2">
                                {nextOptions.map((nextStatus) => (
                                  <button
                                    key={nextStatus}
                                    onClick={() => handleStatusChange(order.orderNumber, nextStatus)}
                                    className={`px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap
                                      ${nextStatus === 'cancelled'
                                        ? 'border-red-200 text-red-600 hover:bg-red-50 bg-white'
                                        : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 bg-white'
                                      }`}
                                  >
                                    <i className={`${STATUS_ICON[nextStatus]} w-4 h-4 flex items-center justify-center`}></i>
                                    {STATUS_LABEL[nextStatus]}으로 변경
                                  </button>
                                ))}
                                <button
                                  onClick={() => setChangingStatus(null)}
                                  className="px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-gray-200 text-gray-500 hover:bg-gray-100 bg-white transition-all cursor-pointer whitespace-nowrap"
                                >
                                  취소
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* 최종 상태 안내 */}
                      {nextOptions.length === 0 && (
                        <div className={`rounded-xl p-4 flex items-center gap-3
                          ${order.status === 'delivered' ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                            ${order.status === 'delivered' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                            <i className={`${STATUS_ICON[order.status]} w-4 h-4 flex items-center justify-center
                              ${order.status === 'delivered' ? 'text-emerald-600' : 'text-red-500'}`}></i>
                          </div>
                          <div>
                            <p className={`text-sm font-bold ${order.status === 'delivered' ? 'text-emerald-700' : 'text-red-600'}`}>
                              {order.status === 'delivered' ? '배송이 완료된 주문입니다.' : '취소된 주문입니다.'}
                            </p>
                            <p className={`text-xs mt-0.5 ${order.status === 'delivered' ? 'text-emerald-600' : 'text-red-500'}`}>
                              {order.status === 'delivered' ? '상품을 잘 받으셨나요? 문의사항이 있으시면 고객센터로 연락해주세요.' : '주문이 취소 처리되었습니다.'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* 주문 상품 목록 */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">주문 상품</p>
                        <div className="space-y-3">
                          {order.items.map((item) => (
                            <div key={item.id} className="flex items-center gap-3">
                              <div className="w-14 h-14 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                                <p className="text-xs text-gray-500">{item.category} · 수량 {item.quantity}개</p>
                              </div>
                              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                <p className="text-sm font-bold text-gray-800 whitespace-nowrap">{formatPrice(item.price * item.quantity)}원</p>
                                {order.status === 'delivered' && (
                                  reviewedItems.has(item.id) ? (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold whitespace-nowrap">
                                      <i className="ri-checkbox-circle-fill w-3 h-3 flex items-center justify-center" />
                                      리뷰 완료
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setReviewModalItem({ id: item.id, name: item.name, image: item.image })}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded-full text-xs font-semibold hover:bg-amber-100 transition-colors whitespace-nowrap cursor-pointer"
                                    >
                                      <i className="ri-pencil-line w-3 h-3 flex items-center justify-center" />
                                      리뷰 작성
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 결제 & 배송 정보 */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">결제 정보</p>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">결제 방법</span>
                            <span className="font-semibold text-gray-800">{order.paymentMethod}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">상품 금액</span>
                            <span className="text-gray-800">{formatPrice(order.totalPrice)}원</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">배송비</span>
                            <span className="text-emerald-600 font-semibold">무료</span>
                          </div>
                          <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                            <span className="font-bold text-gray-900">총 결제금액</span>
                            <span className="font-bold text-emerald-600">{formatPrice(order.totalPrice)}원</span>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">배송 정보</p>
                          <div className="text-sm space-y-1.5">
                            <div className="flex gap-2">
                              <span className="text-gray-500 flex-shrink-0">받는 분</span>
                              <span className="font-semibold text-gray-800">{order.name}</span>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-gray-500 flex-shrink-0">연락처</span>
                              <span className="text-gray-800">{order.phone}</span>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-gray-500 flex-shrink-0">주소</span>
                              <span className="text-gray-800 text-xs leading-relaxed">{order.shippingAddress}</span>
                            </div>
                            {order.memo && (
                              <div className="flex gap-2">
                                <span className="text-gray-500 flex-shrink-0">메모</span>
                                <span className="text-gray-800 text-xs">{order.memo}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 계좌이체 입금 안내 */}
                      {order.paymentMethod === '계좌이체' && order.status === 'pending' && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <p className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                            <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
                            입금 안내
                          </p>
                          <div className="text-sm text-amber-700 space-y-1">
                            <p><span className="font-semibold">은행:</span> IBK기업은행</p>
                            <p><span className="font-semibold">계좌번호:</span> 010-5330-8579</p>
                            <p><span className="font-semibold">예금주:</span> 신경진</p>
                          </div>
                          <p className="text-xs text-amber-600 mt-2 pt-2 border-t border-amber-200">
                            입금자명에 주문번호({order.orderNumber})를 기재해주세요.
                          </p>
                        </div>
                      )}

                      {/* 액션 버튼 */}
                      <div className="flex gap-3 pt-2 flex-wrap">
                        {/* 고객용 주문 취소 버튼 */}
                        {CANCELLABLE_STATUSES.includes(order.status) && (
                          <button
                            onClick={() => {
                              setCancelPopup({ orderNumber: order.orderNumber });
                              setCancelReason('단순 변심');
                              setCancelReasonEtc('');
                            }}
                            className="flex-1 py-2.5 border-2 border-red-200 text-red-500 rounded-full text-sm font-semibold text-center hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5"
                          >
                            <i className="ri-close-circle-line w-4 h-4 flex items-center justify-center"></i>
                            주문 취소
                          </button>
                        )}
                        <Link to="/support" className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-full text-sm font-semibold text-center hover:border-emerald-500 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">
                          문의하기
                        </Link>
                        {/* 재주문 버튼 */}
                        <button
                          type="button"
                          onClick={() => handleReorder(order)}
                          disabled={reorderingOrder === order.orderNumber}
                          className="flex-1 py-2.5 bg-emerald-600 text-white rounded-full text-sm font-semibold text-center hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {reorderingOrder === order.orderNumber ? (
                            <>
                              <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                              담는 중...
                            </>
                          ) : (
                            <>
                              <i className="ri-shopping-cart-2-line w-4 h-4 flex items-center justify-center"></i>
                              재주문하기
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <PageFooter />
    </>
  );
}
